import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import usePrintStore from '../../store/printStore';
import { FiMapPin } from 'react-icons/fi';
import IconWrapper from '../print-steps/IconWrapper';
import { useAuthStore } from '../../store/authStore';
import { Shop, searchShops, getAllOnlineShops } from '../../lib/supabase/api/shopService';
import useDebounce from '../../hooks/useDebounce';
import { toast } from 'react-hot-toast';
import { setShopPricingFromObject } from '../../utils/priceCalculations';

// Import sub-components
import SearchInput from './SearchInput';
import LocationButton from './LocationButton';
import ShopList from './ShopList';

// Add this prop to receive location permission response from parent
interface ShopSelectionCardProps {
  showLocationModal?: (show: boolean) => void;
  locationResponse?: boolean | null;
}

const ShopSelectionCard: React.FC<ShopSelectionCardProps> = ({ 
  showLocationModal, 
  locationResponse 
}) => {
  const navigate = useNavigate();
  const { selectedShop, setSelectedShop, setCurrentStep, documents, removeDocument, printSettings } = usePrintStore();
  const { user, signInWithGoogle, loading, setPreviousPrintStep } = useAuthStore();
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Shop state
  const [shops, setShops] = useState<Shop[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  
  // Commented out location-related state and logic
  // const [locationShared, setLocationShared] = useState(false);
  // const [pendingLocationRequest, setPendingLocationRequest] = useState(false);
  
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const initialLoadCompleted = useRef(false);
  
  // Clear selected shop when component mounts (when user navigates to this step)
  useEffect(() => {
    setSelectedShop(null);
  }, [setSelectedShop]);
  
  // Initial load - fetch all online shops
  useEffect(() => {
    if (initialLoadCompleted.current) return;
    initialLoadCompleted.current = true;

    console.log('Fetching all online shops...');
    loadAllOnlineShops();
  }, []);

  // Commented out location-related logic
  // useEffect(() => {
  //   if (initialLoadCompleted.current) return;
  //   initialLoadCompleted.current = true;
  //   // ...existing code...
  // }, []);

  // Commented out location response handling
  // useEffect(() => {
  //   if (locationResponse === null || !pendingLocationRequest) return;
  //   // ...existing code...
  // }, [locationResponse]);

  // Commented out location request function
  // const requestUserLocation = () => {
  //   // ...existing code...
  // };

  // Fetch all online shops
  const loadAllOnlineShops = async () => {
    setIsLoadingShops(true);
    try {
      const allShops = await getAllOnlineShops();
      console.log('Fetched all online shops:', allShops);
      
      // Debug: Check pricing data in shops
      allShops.forEach(shop => {
        console.log(`Shop ${shop.name} pricing:`, {
          id: shop.id,
          monochrome_cost: shop.monochrome_cost,
          color_cost: shop.color_cost,
          pricing: shop.pricing,
          hasPricing: !!shop.pricing,
          pricingKeys: shop.pricing ? Object.keys(shop.pricing) : []
        });
      });
      
      setShops(allShops);
    } catch (error) {
      console.error('Error fetching all online shops:', error);
      setShops([]);
    } finally {
      setIsLoadingShops(false);
    }
  };

  // Search handler
  useEffect(() => {
    if (!debouncedSearchQuery) {
      console.log('Search query cleared, fetching all online shops...');
      loadAllOnlineShops();
      return;
    }

    const performSearch = async () => {
      setIsLoadingShops(true);
      try {
        const results = await searchShops(debouncedSearchQuery);
        console.log('Search results:', results);
        setShops(results);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsLoadingShops(false);
      }
    };

    performSearch();
  }, [debouncedSearchQuery]);

  // Commented out location button handler
  // const handleUseCurrentLocation = () => {
  //   // ...existing code...
  // };

  const handleBack = () => {
    const isPrintBazaar = documents.some(doc => doc.source === 'print_bazar');
    
    if (isPrintBazaar) {
      // For print_bazar flow: navigate to MyAccounts purchases tab
      navigate('/my-account?tab=purchases');
      // Remove documents and reset step
      documents.forEach(doc => removeDocument(doc.id));
      setCurrentStep(0);
    } else {
      // For normal flow: go back to print settings
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleShopSelection = (shopId: string) => {
    // Toggle selection: if the shop is already selected, deselect it
    if (selectedShop === shopId) {
      setSelectedShop(null);
    } else {
      setSelectedShop(shopId);
      
      // Set pricing immediately from shop object to avoid async DB call
      const shop = shops.find(s => s.id === shopId);
      if (shop?.pricing) {
        console.log('[handleShopSelection] Setting pricing from shop object:', shop.pricing);
        setShopPricingFromObject(shop.pricing);
      } else {
        console.warn('[handleShopSelection] Shop has no pricing data:', shop);
      }
    }
  };

  const handleContinue = () => {
    if (!selectedShop) {
      toast.error('Please select a shop to continue');
      return;
    }

    if (user) {
      // User is authenticated, continue to next step
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // User is not authenticated, save current step and redirect to sign in
      setPreviousPrintStep(2);
      setIsRedirecting(true);
      signInWithGoogle();
    }
  };

  return (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md w-full max-w-2xl mx-auto">
      <h2 className="text-xl sm:text-2xl font-semibold mb-6 text-blue-600 flex items-center">
        <IconWrapper icon={FiMapPin} className="mr-2" />
        Select Print Shop
      </h2>

      <div className="mb-8">
        <p className="text-gray-500 mb-6">Choose where you'd like to pick up your prints</p>
        
        {/* Search input component */}
        <SearchInput 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery} 
        />
        
        {/* Location button component 
        
         <LocationButton 
          locationShared={false} // Updated to false since location is not used
          pendingLocationRequest={false} // Updated to false since location is not used
          isLoadingShops={isLoadingShops}
          onRequestLocation={() => {}} // No-op since location is not used
        /> */}
        
        {/* Shop list component with more margin */}
        <div className="mt-6">
          <ShopList 
            shops={shops}
            isLoading={isLoadingShops}
            selectedShopId={selectedShop}
            setSelectedShop={handleShopSelection}
            locationShared={false} // Updated to false since location is not used
            paperType={documents.length > 0 && documents[0]?.id ? printSettings[documents[0].id]?.paperType : 'A4'}
            printSettings={printSettings} // Pass all print settings for compatibility check
          />
        </div>
      </div>

      {!user && (
        <div className="mb-6 p-4 sm:p-5 bg-yellow-50 border border-yellow-300 rounded-md text-sm sm:text-base">
          <p className="text-yellow-800">
            <strong>Note:</strong> You need to sign in to proceed to checkout.
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-6 sticky bottom-0 bg-white pt-4 pb-4 border-t sm:border-0 mt-6">
        <button
          onClick={handleBack}
          className="px-4 sm:px-6 py-3 bg-gray-300 hover:bg-gray-400 rounded-lg text-sm sm:text-base order-2 sm:order-1"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          className={`px-4 sm:px-6 py-3 rounded-lg text-sm sm:text-base w-full sm:w-auto order-1 sm:order-2 ${
            loading || isRedirecting || isLoadingShops || !selectedShop || shops.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
          disabled={loading || isRedirecting || isLoadingShops || !selectedShop || shops.length === 0}
        >
          {loading || isRedirecting 
            ? 'Please wait...' 
            : user 
              ? 'Continue' 
              : 'Sign in to Continue'
          }
        </button>
      </div>
    </div>
  );
};

export default ShopSelectionCard;